import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import { Button, Checkbox, Input, List, Radio, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";
import {
  codeOf,
  normalizeClassifications,
  type Classification,
  type Classifications,
  type CsiSystem,
  type CsiTerm
} from "design-scope";
import { useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import type { HTTPClientResponse } from "services/ciq-client";
import {
  CLASSIFICATION_ITEMS_BY_IDS,
  SEARCH_CLASSIFICATION_ITEMS
} from "../queries";
import {
  buildClassificationByIdsVariables,
  buildClassificationSearchVariables,
  buildCustomClassificationSearchVariables,
  mapGqlClassificationItem,
  mapGqlCustomClassificationItem,
  walkClassificationAncestry,
  type CustomClassificationHit,
  type GqlClassificationItem
} from "../shared/classification-catalog";
import { ClassBadge } from "./scope-ui";

const SYSTEM_LABEL: Record<CsiSystem, string> = {
  UF: "UniFormat",
  MF: "MasterFormat"
};

const SLOT_KEY: Record<CsiSystem, "uf" | "mf"> = { UF: "uf", MF: "mf" };

const SEARCH_DEBOUNCE_MS = 300;

type LazyQuery = (variables?: unknown) => Promise<HTTPClientResponse>;

function rowsFromLazyResponse(res: HTTPClientResponse): CsiTerm[] {
  if (!res.success) return [];
  // CIQClient wraps the GraphQL JSON: { data: { classification_items }, errors? }
  const body = res.data;
  if (body?.errors?.length) return [];
  const rows = (body?.data?.classification_items ??
    []) as GqlClassificationItem[];
  return rows
    .map(mapGqlClassificationItem)
    .filter((t): t is CsiTerm => t !== null);
}

function customRowsFromLazyResponse(
  res: HTTPClientResponse
): CustomClassificationHit[] {
  if (!res.success) return [];
  const body = res.data;
  if (body?.errors?.length) return [];
  const rows = (body?.data?.classification_items ??
    []) as GqlClassificationItem[];
  return rows
    .map(mapGqlCustomClassificationItem)
    .filter((t): t is CustomClassificationHit => t !== null);
}

/** Small uppercase caption over each slot. */
function SlotLabel({ children }: { children: string }) {
  return (
    <div className="mb-1">
      <Typography.Text
        type="secondary"
        strong
        style={{ fontSize: 11, letterSpacing: 0.3 }}
      >
        {children}
      </Typography.Text>
    </div>
  );
}

function toClassification(
  t: CsiTerm,
  path?: Classification["path"]
): Classification {
  return {
    system: t.system,
    code: t.number,
    title: t.title,
    termId: t.id,
    parentId: t.parentId,
    path: path ?? [{ number: t.number, title: t.title }]
  };
}

function toCustomLabel(hit: CustomClassificationHit): Classification {
  return {
    system: "CUSTOM",
    code: "",
    title: hit.title,
    termId: hit.id
  };
}

function toNewCustomLabel(title: string): Classification {
  return { system: "CUSTOM", code: "", title };
}

/**
 * One standard's slot: a search box until a code resolves, then the code
 * itself — which doubles as the control that clears back to the search box.
 */
function SystemSlot(props: {
  system: CsiSystem;
  value?: Classification;
  ancestry: string;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Bulk-edit: checkbox that enables this slot for replace. */
  replaceChecked?: boolean;
  onReplaceChange?: (checked: boolean) => void;
  searchItems: LazyQuery;
  fetchByIds: LazyQuery;
  onPick: (c: Classification) => void;
  onClear: () => void;
}) {
  const {
    system,
    value,
    ancestry,
    autoFocus,
    disabled = false,
    placeholder,
    replaceChecked,
    onReplaceChange,
    searchItems,
    fetchByIds,
    onPick,
    onClear
  } = props;
  const showReplace = typeof replaceChecked === "boolean" && onReplaceChange;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CsiTerm[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeItemRef = useRef<HTMLSpanElement | null>(null);
  const searchGen = useRef(0);
  const pickGen = useRef(0);
  const slot = SLOT_KEY[system];

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const loadByIds = async (ids: string[]): Promise<CsiTerm[]> => {
    const variables = buildClassificationByIdsVariables(ids);
    if (!variables) return [];
    const res = await fetchByIds(variables);
    return rowsFromLazyResponse(res);
  };

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        const gen = ++searchGen.current;
        const variables = buildClassificationSearchVariables(q, system);
        if (!variables) {
          if (gen !== searchGen.current) return;
          setResults([]);
          setLoading(false);
          setError(null);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const res = await searchItems(variables);
          if (gen !== searchGen.current) return;
          if (!res.success || res.data?.errors?.length) {
            setResults([]);
            setError("Could not search classifications.");
            return;
          }
          setResults(rowsFromLazyResponse(res));
          setHighlight(0);
        } catch {
          if (gen !== searchGen.current) return;
          setResults([]);
          setError("Could not search classifications.");
        } finally {
          if (gen === searchGen.current) setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS),
    [system, searchItems]
  );

  useEffect(
    () => () => {
      runSearch.cancel();
      searchGen.current += 1;
      pickGen.current += 1;
    },
    [runSearch]
  );

  const search = (q: string) => {
    setQuery(q);
    if (!q.trim()) {
      runSearch.cancel();
      searchGen.current += 1;
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    runSearch(q);
  };

  const pick = (t: CsiTerm) => {
    const gen = ++pickGen.current;
    searchGen.current += 1;
    runSearch.cancel();
    onPick(toClassification(t));
    setResults([]);
    setQuery("");
    setError(null);
    setLoading(false);
    void walkClassificationAncestry(t, loadByIds)
      .then((path) => {
        if (gen !== pickGen.current) return;
        onPick(toClassification(t, path));
      })
      .catch(() => {
        /* keep the leaf-only path already applied */
      });
  };

  const clear = () => {
    pickGen.current += 1;
    searchGen.current += 1;
    runSearch.cancel();
    onClear();
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault(); // never submit the surrounding modal form
      if (results.length && results[highlight]) pick(results[highlight]);
    }
  };

  const slotHeader = (
    <>
      <SlotLabel>{SYSTEM_LABEL[system]}</SlotLabel>
      {showReplace && (
        <div className="mb-2">
          <Checkbox
            checked={replaceChecked}
            data-testid={`csi-replace-${slot}`}
            onChange={(e) => onReplaceChange?.(e.target.checked)}
          >
            Replace existing code across all selected items.
          </Checkbox>
        </div>
      )}
    </>
  );

  if (value) {
    return (
      <div>
        {slotHeader}
        <div
          className={`dsm-class-slot rounded-lg px-3 py-2${
            disabled ? " opacity-50" : ""
          }`}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`csi-change-${slot}`}
              title={`Click to pick a different ${SYSTEM_LABEL[system]} code`}
              className="dsm-class-trigger flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left"
              disabled={disabled}
              onClick={clear}
            >
              <ClassBadge system={value.system} code={value.code} />
              <Typography.Text className="min-w-0 flex-1 truncate">
                {value.title}
              </Typography.Text>
            </button>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              data-testid={`csi-clear-${slot}`}
              disabled={disabled}
              onClick={clear}
            />
          </div>
          {ancestry && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {ancestry}
            </Typography.Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {slotHeader}
      <Input
        allowClear={!disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus && !disabled}
        data-testid={`csi-search-${slot}`}
        value={query}
        disabled={disabled}
        placeholder={placeholder ?? `Search ${SYSTEM_LABEL[system]}…`}
        onChange={(e) => search(e.target.value)}
        onKeyDown={onSearchKey}
      />
      {loading && (
        <div className="mt-1">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Searching…
          </Typography.Text>
        </div>
      )}
      {error && (
        <div className="mt-1">
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {error}
          </Typography.Text>
        </div>
      )}
      {results.length > 0 && (
        <List
          size="small"
          bordered
          className="mt-1 overflow-y-auto"
          style={{ maxHeight: 240 }}
          dataSource={results}
          renderItem={(t, idx) => {
            const active = highlight === idx;
            return (
              <List.Item
                className="cursor-pointer"
                data-testid={`csi-result-${slot}-${idx}`}
                onClick={() => pick(t)}
                onMouseEnter={() => setHighlight(idx)}
                style={
                  active ? { background: "var(--ciq-gray-100)" } : undefined
                }
              >
                <span
                  ref={active ? activeItemRef : undefined}
                  className="inline-flex items-center gap-2"
                >
                  <ClassBadge system={t.system} code={t.number} />
                  <Typography.Text>{t.title}</Typography.Text>
                </span>
              </List.Item>
            );
          }}
        />
      )}
      {!loading && !error && query.trim() && results.length === 0 && (
        <div className="mt-1">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            No matching {SYSTEM_LABEL[system]} code.
          </Typography.Text>
        </div>
      )}
    </div>
  );
}

/**
 * Custom format: search existing custom titles, or create a new title-only
 * label via Create row / Enter / blur when there is no exact match.
 */
function CustomFormatSlot(props: {
  value?: Classification;
  searchItems: LazyQuery;
  onPick: (c: Classification) => void;
  onClear: () => void;
  disabled?: boolean;
  placeholder?: string;
  replaceChecked?: boolean;
  onReplaceChange?: (checked: boolean) => void;
}) {
  const {
    value,
    searchItems,
    onPick,
    onClear,
    disabled = false,
    placeholder,
    replaceChecked,
    onReplaceChange
  } = props;
  const showReplace = typeof replaceChecked === "boolean" && onReplaceChange;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomClassificationHit[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeItemRef = useRef<HTMLSpanElement | null>(null);
  const searchGen = useRef(0);
  const suppressBlurRef = useRef(false);

  const trimmed = query.trim();
  const hasExactMatch = results.some(
    (r) => r.title.toLowerCase() === trimmed.toLowerCase()
  );
  const showCreate = Boolean(trimmed) && !loading && !error && !hasExactMatch;
  const optionCount = results.length + (showCreate ? 1 : 0);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        const gen = ++searchGen.current;
        const variables = buildCustomClassificationSearchVariables(q);
        if (!variables) {
          if (gen !== searchGen.current) return;
          setResults([]);
          setLoading(false);
          setError(null);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const res = await searchItems(variables);
          if (gen !== searchGen.current) return;
          if (!res.success || res.data?.errors?.length) {
            setResults([]);
            setError("Could not search classifications.");
            return;
          }
          setResults(customRowsFromLazyResponse(res));
          setHighlight(0);
        } catch {
          if (gen !== searchGen.current) return;
          setResults([]);
          setError("Could not search classifications.");
        } finally {
          if (gen === searchGen.current) setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS),
    [searchItems]
  );

  useEffect(
    () => () => {
      runSearch.cancel();
      searchGen.current += 1;
    },
    [runSearch]
  );

  const resetSearchUi = () => {
    setResults([]);
    setQuery("");
    setError(null);
    setLoading(false);
  };

  const search = (q: string) => {
    setQuery(q);
    if (!q.trim()) {
      runSearch.cancel();
      searchGen.current += 1;
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    runSearch(q);
  };

  const pickExisting = (hit: CustomClassificationHit) => {
    searchGen.current += 1;
    runSearch.cancel();
    onPick(toCustomLabel(hit));
    resetSearchUi();
  };

  const createNew = (title: string) => {
    const t = title.trim();
    if (!t) return;
    searchGen.current += 1;
    runSearch.cancel();
    onPick(toNewCustomLabel(t));
    resetSearchUi();
  };

  const clear = () => {
    searchGen.current += 1;
    runSearch.cancel();
    onClear();
  };

  const commitHighlightedOrNew = () => {
    if (!trimmed) return;
    if (highlight < results.length && results[highlight]) {
      pickExisting(results[highlight]);
      return;
    }
    if (showCreate || !hasExactMatch) {
      createNew(trimmed);
    }
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!optionCount) return;
      setHighlight((h) => Math.min(h + 1, optionCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitHighlightedOrNew();
    }
  };

  const onSearchBlur = () => {
    if (suppressBlurRef.current) {
      suppressBlurRef.current = false;
      return;
    }
    if (!trimmed) return;
    const exact = results.find(
      (r) => r.title.toLowerCase() === trimmed.toLowerCase()
    );
    if (exact) {
      pickExisting(exact);
      return;
    }
    createNew(trimmed);
  };

  const onListMouseDown = () => {
    suppressBlurRef.current = true;
  };

  const slotHeader = (
    <>
      <SlotLabel>Custom format</SlotLabel>
      {showReplace && (
        <div className="mb-2">
          <Checkbox
            checked={replaceChecked}
            data-testid="csi-replace-custom"
            onChange={(e) => onReplaceChange?.(e.target.checked)}
          >
            Replace existing code across all selected items.
          </Checkbox>
        </div>
      )}
    </>
  );

  if (value?.title.trim()) {
    return (
      <div>
        {slotHeader}
        <div
          className={`dsm-class-slot rounded-lg px-3 py-2${
            disabled ? " opacity-50" : ""
          }`}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="csi-change-custom"
              title="Click to pick a different custom format"
              className="dsm-class-trigger flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left"
              disabled={disabled}
              onClick={clear}
            >
              <ClassBadge system="CUSTOM" code={value.title} />
            </button>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              data-testid="csi-clear-custom"
              disabled={disabled}
              onClick={clear}
            />
          </div>
        </div>
      </div>
    );
  }

  const showList = !disabled && (results.length > 0 || showCreate);

  return (
    <div>
      {slotHeader}
      <Input
        allowClear={!disabled}
        data-testid="csi-search-custom"
        value={query}
        disabled={disabled}
        placeholder={placeholder ?? "Search custom format…"}
        onChange={(e) => search(e.target.value)}
        onKeyDown={onSearchKey}
        onBlur={disabled ? undefined : onSearchBlur}
      />
      {loading && (
        <div className="mt-1">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Searching…
          </Typography.Text>
        </div>
      )}
      {error && (
        <div className="mt-1">
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {error}
          </Typography.Text>
        </div>
      )}
      {showList && (
        <List
          size="small"
          bordered
          className="mt-1 overflow-y-auto"
          style={{ maxHeight: 240 }}
          dataSource={[
            ...results.map((hit) => ({ kind: "hit" as const, hit })),
            ...(showCreate ? [{ kind: "create" as const, title: trimmed }] : [])
          ]}
          renderItem={(item, idx) => {
            const active = highlight === idx;
            if (item.kind === "create") {
              return (
                <List.Item
                  className="cursor-pointer"
                  data-testid="csi-custom-create"
                  onMouseDown={onListMouseDown}
                  onClick={() => createNew(item.title)}
                  onMouseEnter={() => setHighlight(idx)}
                  style={
                    active ? { background: "var(--ciq-gray-100)" } : undefined
                  }
                >
                  <span
                    ref={active ? activeItemRef : undefined}
                    className="inline-flex items-center gap-2"
                  >
                    <Typography.Text>
                      Create &quot;{item.title}&quot;
                    </Typography.Text>
                  </span>
                </List.Item>
              );
            }
            return (
              <List.Item
                className="cursor-pointer"
                data-testid={`csi-result-custom-${idx}`}
                onMouseDown={onListMouseDown}
                onClick={() => pickExisting(item.hit)}
                onMouseEnter={() => setHighlight(idx)}
                style={
                  active ? { background: "var(--ciq-gray-100)" } : undefined
                }
              >
                <span
                  ref={active ? activeItemRef : undefined}
                  className="inline-flex items-center gap-2"
                >
                  <Typography.Text>{item.hit.title}</Typography.Text>
                </span>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}

export type ClassificationPickerBulkReplace = {
  replaceUf: boolean;
  replaceMf: boolean;
  replaceCustom: boolean;
  onReplaceUfChange: (checked: boolean) => void;
  onReplaceMfChange: (checked: boolean) => void;
  onReplaceCustomChange: (checked: boolean) => void;
};

export default function ClassificationPicker(props: {
  value?: Classifications;
  onChange: (c: Classifications | undefined) => void;
  /**
   * Bulk Edit Classification: replace checkbox per Standard / Custom slot.
   * Unchecked slots are disabled.
   */
  bulkReplace?: ClassificationPickerBulkReplace;
}) {
  const { value, onChange, bulkReplace } = props;
  const [searchItems] = useCIQLazyQuery(SEARCH_CLASSIFICATION_ITEMS);
  const [fetchByIds] = useCIQLazyQuery(CLASSIFICATION_ITEMS_BY_IDS);

  const ancestryOf = (system: CsiSystem): string => {
    const hit = codeOf(value, system);
    if (!hit?.path || hit.path.length < 2) return "";
    return hit.path.map((n) => n.title || n.number).join(" ▸ ");
  };

  const setSlot = (system: CsiSystem, c: Classification | undefined) =>
    onChange(
      normalizeClassifications({
        ...value,
        [SLOT_KEY[system]]: c,
        label: undefined
      })
    );

  const setCustomLabel = (c: Classification | undefined) =>
    onChange(c ? normalizeClassifications({ label: c }) : undefined);

  const coded = Boolean(value?.uf || value?.mf);
  // Only the tab is local: the value itself stays the one source of truth,
  // and a label only replaces the codes once something is typed into it.
  const labelOnly = Boolean(value?.label) && !coded;
  const [format, setFormat] = useState<"standard" | "custom">(
    labelOnly ? "custom" : "standard"
  );
  // The editor hands the value in after its first render, so a label-only
  // item has to pull the tab across once it arrives.
  useEffect(() => {
    if (labelOnly) setFormat("custom");
  }, [labelOnly]);

  const standardSlots = (["UF", "MF"] as CsiSystem[]).map((system) => {
    const isUf = system === "UF";
    const replaceChecked = isUf
      ? bulkReplace?.replaceUf
      : bulkReplace?.replaceMf;
    const slotDisabled = Boolean(bulkReplace) && !replaceChecked;
    return (
      <SystemSlot
        key={`${system}-${replaceChecked ? "on" : "off"}`}
        system={system}
        value={codeOf(value, system)}
        ancestry={ancestryOf(system)}
        disabled={slotDisabled}
        placeholder={
          bulkReplace ? `Search and Select ${SYSTEM_LABEL[system]}` : undefined
        }
        replaceChecked={bulkReplace ? replaceChecked : undefined}
        onReplaceChange={
          bulkReplace
            ? isUf
              ? bulkReplace.onReplaceUfChange
              : bulkReplace.onReplaceMfChange
            : undefined
        }
        searchItems={searchItems}
        fetchByIds={fetchByIds}
        onPick={(c) => setSlot(system, c)}
        onClear={() => setSlot(system, undefined)}
      />
    );
  });

  const customSlot = (
    <CustomFormatSlot
      key={`custom-${bulkReplace?.replaceCustom ? "on" : "off"}`}
      value={value?.label}
      searchItems={searchItems}
      disabled={Boolean(bulkReplace) && !bulkReplace?.replaceCustom}
      placeholder={bulkReplace ? "Search and Select Custom format" : undefined}
      replaceChecked={bulkReplace ? bulkReplace.replaceCustom : undefined}
      onReplaceChange={bulkReplace?.onReplaceCustomChange}
      onPick={setCustomLabel}
      onClear={() => setCustomLabel(undefined)}
    />
  );

  return (
    <div className="flex flex-col gap-3" data-testid="csi-slot">
      <Radio.Group
        optionType="button"
        size="small"
        className="self-start"
        data-testid="csi-format"
        value={format}
        onChange={(e) => setFormat(e.target.value as "standard" | "custom")}
        options={[
          { label: "Standard", value: "standard" },
          { label: "Custom", value: "custom" }
        ]}
      />
      {format === "standard" ? standardSlots : customSlot}
    </div>
  );
}
